/**
 * Single interview session detail — full transcript + scorecard.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Bot, User as UserIcon, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function InterviewSessionDetail() {
  const { id } = useParams();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/realtime/sessions/${id}`)
      .then((r) => setSession(r.data))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (!session) return <div className="text-sm text-muted-foreground">Session not found.</div>;

  const sc = session.scorecard || {};
  const dims = sc.dimensions || {};

  return (
    <div className="max-w-4xl" data-testid="interview-session-detail">
      <PageHeader
        eyebrow={`Session · ${session.interview_type} · ${session.difficulty}`}
        title={session.role || "Voice interview"}
        subtitle={new Date(session.started_at).toLocaleString() + " · " + Math.round((session.duration_seconds || 0) / 60) + " min"}
      />

      <Button asChild variant="outline" size="sm" className="mb-4">
        <Link to="/app/interview/history" className="inline-flex items-center gap-1"><ArrowLeft size={14} /> Back to history</Link>
      </Button>

      {sc.overall_score !== undefined && (
        <div className="bg-card border border-border rounded-xl p-6 mb-6">
          <div className="flex items-center gap-4 mb-3">
            <div className="w-20 h-20 rounded-full flex items-center justify-center bg-primary/10 border-4 border-primary/30">
              <div className="text-3xl font-black">{sc.overall_score}</div>
            </div>
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Verdict</div>
              <div className="text-xl font-display font-bold">{sc.verdict || "—"}</div>
              <div className="text-sm text-muted-foreground mt-1">{sc.summary}</div>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            {Object.entries(dims).map(([k, v]) => (
              <div key={k} className="p-3 bg-muted/30 rounded-lg">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{k.replace(/_/g, " ")}</div>
                <div className="text-2xl font-bold">{v?.score ?? "—"}</div>
                <div className="text-xs text-muted-foreground line-clamp-2">{v?.note}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-card border border-border rounded-xl p-4">
        <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">Full transcript</div>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
          {(session.transcript || []).map((t, i) => (
            <div key={i} className={`flex gap-2 ${t.speaker === "user" ? "flex-row-reverse" : ""}`}>
              <div className={`h-7 w-7 shrink-0 rounded-full flex items-center justify-center ${t.speaker === "user" ? "bg-emerald-500/15 text-emerald-500" : "bg-primary/15 text-primary"}`}>
                {t.speaker === "user" ? <UserIcon size={14} /> : <Bot size={14} />}
              </div>
              <div className={`text-sm rounded-lg px-3 py-2 max-w-[80%] ${t.speaker === "user" ? "bg-emerald-500/10" : "bg-muted/40"}`}>
                {t.text}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
